/**
 * Utility functions for handling country flags dynamically
 * All country codes and emojis come from the database via the backend API
 */
import React from 'react';

/**
 * Generate flag URL from country code using flagcdn.com
 * @param countryCode - 2-letter country code (e.g., 'US', 'UK', 'SA')
 * @param size - Flag size, default is '48x36'
 * @returns Full CDN URL for the flag image
 */
export const getFlagUrl = (countryCode: string, size: string = '48x36'): string => {
  if (!countryCode || countryCode === 'undefined' || countryCode.trim() === '') return '';
  return `https://flagcdn.com/${size}/${countryCode.toLowerCase()}.png`;
};

/**
 * Component for rendering country flag with automatic fallback to emoji
 */
interface FlagImageProps {
  countryCode: string;
  countryName: string;
  fallbackEmoji?: string;
  className?: string;
  size?: string;
}

/**
 * Get flag props for img element with error handling
 * @param countryCode - 2-letter country code
 * @param fallbackEmoji - Emoji to show if image fails to load
 * @returns Object with src and onError handler
 */
export const getFlagImageProps = (
  countryCode: string,
  fallbackEmoji: string = '🌍'
) => {
  return {
    src: getFlagUrl(countryCode),
    onError: (e: React.SyntheticEvent<HTMLImageElement>) => {
      const target = e.currentTarget;
      target.style.display = 'none';
      const parent = target.parentNode as HTMLElement;
      if (parent && !parent.querySelector('.flag-fallback')) {
        const fallbackSpan = document.createElement('span');
        fallbackSpan.className = 'flag-fallback';
        fallbackSpan.textContent = fallbackEmoji;
        parent.appendChild(fallbackSpan);
      }
    }
  };
};

/**
 * Renders a country flag image with fallback to emoji
 * All data comes from the database - no hardcoded mappings
 * @param countryCode - 2-letter country code from database (e.g., 'US', 'UK', 'SA')
 * @param fallbackEmoji - Emoji flag from database (e.g., '🇺🇸', '🇬🇧', '🇸🇦')
 * @param countryName - Full country name for alt text
 * @param className - CSS classes for the img element
 * @returns JSX element with flag image or emoji fallback
 */
export const renderCountryFlag = (
  countryCode: string,
  fallbackEmoji: string,
  countryName: string,
  className: string = 'w-8 h-6 object-cover rounded-sm'
): React.ReactElement => {
  // Use globe emoji as ultimate fallback if database doesn't provide one
  const safeEmoji = fallbackEmoji || '🌍';

  // If we have a valid country code, try to load flag from CDN
  if (countryCode && countryCode.length <= 3) {
    return (
      <img
        src={getFlagUrl(countryCode)}
        alt={`${countryName} flag`}
        className={className}
        onError={(e) => {
          const target = e.currentTarget;
          target.style.display = 'none';
          const parent = target.parentNode as HTMLElement;
          if (parent && !parent.querySelector('.flag-fallback')) {
            const span = document.createElement('span');
            span.className = 'flag-fallback text-xl';
            span.textContent = safeEmoji;
            parent.appendChild(span);
          }
        }}
      />
    );
  }

  // Fallback to emoji if no country code
  return <span className="text-xl">{safeEmoji}</span>;
};